import React, { useEffect, useState } from "react";
import {
  Button,
  Typography,
  Modal,
  Input,
  message,
  Card,
  Row,
  Col,
  Space,
  Tag,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import axios from "axios";
import { BASE_API_URL } from "../../../utils/consts";
import { useSelector } from "react-redux";
import "./BusinessCategories.scss";

const { confirm } = Modal;

interface ExtraIncomeType {
  _id: string;
  clientId: string;
  name: string;
  description: string;
  color: string;
  icon: string;
}

const ExtraIncomeTypesTab: React.FC = () => {
  const userData = useSelector((state: any) => state.userData.user);

  const [types, setTypes] = useState<ExtraIncomeType[]>([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [editingType, setEditingType] = useState<ExtraIncomeType | null>(null);

  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formColor, setFormColor] = useState("#52c41a");

  // Fetch types
  const fetchTypes = async () => {
    if (!userData?.clientId) return;

    try {
      const { data } = await axios.get(
        `${BASE_API_URL}/extra-income-types/${userData.clientId}`
      );
      setTypes(data.types || []);
    } catch (error) {
      console.error("Error fetching extra income types:", error);
      message.error("Greška pri učitavanju tipova dodatnih prihoda");
    }
  };

  useEffect(() => {
    fetchTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.clientId]);

  // Create type
  const createType = async () => {
    if (!formName.trim()) {
      message.warning("Naziv je obavezan");
      return;
    }

    try {
      await axios.post(`${BASE_API_URL}/extra-income-types`, {
        clientId: userData.clientId,
        name: formName,
        description: formDescription,
        color: formColor,
      });

      message.success("Tip dodatnog prihoda kreiran");
      setModalOpen(false);
      resetForm();
      fetchTypes();
    } catch (error: any) {
      console.error(error);
      message.error(error.response?.data?.message || "Greška pri kreiranju");
    }
  };

  // Update type
  const updateType = async () => {
    if (!editingType || !formName.trim()) return;

    try {
      await axios.put(`${BASE_API_URL}/extra-income-types/${editingType._id}`, {
        name: formName,
        description: formDescription,
        color: formColor,
      });

      message.success("Tip dodatnog prihoda ažuriran");
      setEditingType(null);
      resetForm();
      fetchTypes();
    } catch (error: any) {
      console.error(error);
      message.error(error.response?.data?.message || "Greška pri ažuriranju");
    }
  };

  // Delete type
  const deleteType = (typeId: string) => {
    confirm({
      title: "Obrisati tip dodatnog prihoda?",
      content: "Ova akcija je nepovratna.",
      okText: "Obriši",
      cancelText: "Otkaži",
      okType: "danger",
      onOk: async () => {
        try {
          await axios.delete(`${BASE_API_URL}/extra-income-types/${typeId}`);
          message.success("Tip obrisan");
          fetchTypes();
        } catch (error) {
          console.error(error);
          message.error("Greška pri brisanju");
        }
      },
    });
  };

  // Open edit modal
  const openEditModal = (type: ExtraIncomeType) => {
    setEditingType(type);
    setFormName(type.name);
    setFormDescription(type.description);
    setFormColor(type.color);
  };

  // Reset form
  const resetForm = () => {
    setFormName("");
    setFormDescription("");
    setFormColor("#52c41a");
  };

  // Close modal
  const closeModal = () => {
    setModalOpen(false);
    setEditingType(null);
    resetForm();
  };

  const predefinedColors = [
    "#52c41a", // green
    "#1890ff", // blue
    "#faad14", // orange
    "#f5222d", // red
    "#722ed1", // purple
    "#13c2c2", // cyan
    "#eb2f96", // magenta
  ];

  return (
    <div className="bc-settings">
      <Card className="bc-hero">
        <div className="bc-hero__content">
          <div>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Tipovi dodatnih prihoda
            </Typography.Title>
            <Typography.Text type="secondary">
              Definiši tipove dodatnih prihoda koji se ne ubrajaju u regularni
              pazar (coffee sales, tips, private parties, itd.)
            </Typography.Text>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalOpen(true)}
          >
            Dodaj tip
          </Button>
        </div>
        <div className="bc-hero__stats">
          <div>
            <div className="bc-hero__stat-value">{types.length}</div>
            <div className="bc-hero__stat-label">Tipova</div>
          </div>
        </div>
      </Card>

      <Row gutter={[16, 16]} className="bc-grid">
        {types.length === 0 && (
          <Col span={24}>
            <Card className="bc-card" bordered={false}>
              <Typography.Text type="secondary">
                Nema definisanih tipova. Klikni "Dodaj tip" da kreiraš prvi.
              </Typography.Text>
            </Card>
          </Col>
        )}
        {types.map((type) => (
          <Col xs={24} sm={12} md={8} lg={6} key={type._id}>
            <Card
              className="bc-card"
              bordered={false}
              style={{
                borderTop: `4px solid ${type.color}`,
              }}
            >
              <div className="bc-card__header">
                <div style={{ flex: 1 }}>
                  <Typography.Text strong style={{ fontSize: 16 }}>
                    {type.name}
                  </Typography.Text>
                  {type.description && (
                    <div
                      className="bc-card__meta"
                      style={{ marginTop: 4, fontSize: 12 }}
                    >
                      {type.description}
                    </div>
                  )}
                </div>
                <div className="bc-card__actions">
                  <Button
                    size="small"
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => openEditModal(type)}
                  />
                  <Button
                    size="small"
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => deleteType(type._id)}
                  />
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <Tag color={type.color} style={{ marginRight: 0 }}>
                  {type.color}
                </Tag>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* CREATE/EDIT MODAL */}
      <Modal
        title={
          editingType
            ? "Uredi tip dodatnog prihoda"
            : "Dodaj tip dodatnog prihoda"
        }
        open={isModalOpen || !!editingType}
        onCancel={closeModal}
        onOk={editingType ? updateType : createType}
        okText={editingType ? "Sačuvaj" : "Kreiraj"}
        cancelText="Otkaži"
        className="standardHeader standardFooter"
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div>
            <Typography.Text strong>Naziv *</Typography.Text>
            <Input
              placeholder="Npr. Coffee Sales, Tips, Private Party"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              style={{ marginTop: 4 }}
            />
          </div>

          <div>
            <Typography.Text strong>Opis</Typography.Text>
            <Input.TextArea
              placeholder="Kratak opis tipa prihoda (opciono)"
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              rows={3}
              style={{ marginTop: 4 }}
            />
          </div>

          <div>
            <Typography.Text strong>Boja</Typography.Text>
            <div
              style={{
                marginTop: 8,
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              {predefinedColors.map((color) => (
                <div
                  key={color}
                  onClick={() => setFormColor(color)}
                  style={{
                    width: 40,
                    height: 40,
                    backgroundColor: color,
                    borderRadius: 8,
                    cursor: "pointer",
                    border:
                      formColor === color
                        ? "3px solid #000"
                        : "2px solid #d9d9d9",
                    transition: "all 0.2s",
                  }}
                />
              ))}
            </div>
            <Input
              type="color"
              value={formColor}
              onChange={(e) => setFormColor(e.target.value)}
              style={{ marginTop: 8, width: "100%" }}
            />
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default ExtraIncomeTypesTab;
